import { createAsyncThunk } from '@reduxjs/toolkit';
import api from '@/utils/api';
import {
  setProfiles,
  appendProfiles,
  setSpotlightProfiles,
  setPagination,
  setLoading,
  setLoadingMore,
  setLoadingSpotlight,
  setError,
  setHasLocation,
  DiscoveryProfile,
  DiscoveryFilters,
  PaginationState,
} from '../slices/discoverySlice';
import { RootState } from '../Store';

interface SearchProfilesParams {
  filters?: Partial<DiscoveryFilters>;
  page?: number;
  limit?: number;
  append?: boolean; // If true, append to existing profiles instead of replacing
}

interface SearchProfilesResponse {
  profiles: DiscoveryProfile[];
  pagination: PaginationState;
  filters: DiscoveryFilters;
}

interface SpotlightProfilesResponse {
  profiles: DiscoveryProfile[];
}

/**
 * Search profiles by distance and filters
 */
export const searchDiscoveryProfiles = createAsyncThunk(
  'discovery/searchProfiles',
  async (params: SearchProfilesParams, { dispatch, getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const currentFilters = state.discovery.filters;

      // Determine if this is a "load more" operation
      const isLoadingMore = params.append === true;

      if (isLoadingMore) {
        dispatch(setLoadingMore(true));
      } else {
        dispatch(setLoading(true));
      }

      // Merge filters from params with current filters
      const filters = { ...currentFilters, ...params.filters };

      const response = await api.get<SearchProfilesResponse>('/discovery/profiles', {
        params: {
          radius: filters.radius,
          unit: filters.unit,
          search: filters.search || undefined,
          sort: filters.sort,
          locationType: filters.locationType,
          activity: filters.activity || undefined,
          badges: filters.badges.length > 0 ? filters.badges.join(',') : undefined,
          interests: filters.interests && filters.interests.length > 0 ? filters.interests.join(',') : undefined,
          iOwn: filters.iOwn || undefined,
          iKlyxd: filters.iKlyxd || undefined,
          page: params.page || 1,
          limit: params.limit || 20,
        },
      });

      // Either append to existing profiles or replace them
      if (params.append) {
        dispatch(appendProfiles(response.data.profiles));
      } else {
        dispatch(setProfiles(response.data.profiles));
      }

      dispatch(setPagination(response.data.pagination));
      dispatch(setHasLocation(true));

      // If no profiles found and not appending, fetch spotlight profiles
      if (response.data.profiles.length === 0 && !params.append) {
        dispatch(fetchSpotlightProfiles({ limit: 10 }));
      }

      return response.data;
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to search profiles';
      const isLocationError = message.includes('Location required') || error.response?.status === 400;

      dispatch(setError(message));
      if (isLocationError) {
        dispatch(setHasLocation(false));
        // Fetch spotlight profiles as fallback
        dispatch(fetchSpotlightProfiles({ limit: 10 }));
      }
      return rejectWithValue(message);
    } finally {
      dispatch(setLoading(false));
      dispatch(setLoadingMore(false));
    }
  }
);

/**
 * Fetch spotlight/trending profiles for empty state
 */
export const fetchSpotlightProfiles = createAsyncThunk(
  'discovery/fetchSpotlight',
  async (params: { limit?: number }, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setLoadingSpotlight(true));

      const response = await api.get<SpotlightProfilesResponse>('/discovery/spotlight', {
        params: {
          limit: params.limit || 10,
        },
      });

      dispatch(setSpotlightProfiles(response.data.profiles));
      return response.data;
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to load spotlight profiles';
      return rejectWithValue(message);
    } finally {
      dispatch(setLoadingSpotlight(false));
    }
  }
);

/**
 * Load next page of profiles (for infinite scroll)
 */
export const loadMoreProfiles = createAsyncThunk(
  'discovery/loadMore',
  async (_, { dispatch, getState }) => {
    const state = getState() as RootState;
    const { pagination, filters } = state.discovery;

    // Don't load more if already loading or no more results
    if (state.discovery.isLoadingMore || !pagination.hasMore) {
      return;
    }

    // Don't exceed 1000 profile limit
    if (pagination.page * pagination.limit >= 1000) {
      return;
    }

    const nextPage = pagination.page + 1;

    return dispatch(
      searchDiscoveryProfiles({
        filters,
        page: nextPage,
        limit: pagination.limit,
        append: true,
      })
    );
  }
);

/**
 * Refresh search with current filters
 */
export const refreshSearch = createAsyncThunk(
  'discovery/refresh',
  async (_, { dispatch, getState }) => {
    const state = getState() as RootState;
    const { filters, pagination } = state.discovery;

    return dispatch(
      searchDiscoveryProfiles({
        filters,
        page: 1,
        limit: pagination.limit,
        append: false,
      })
    );
  }
);
